import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import {
  ArrowLeft, MessageCircle, ChevronRight, Play, BookOpen, Video,
  FileText, Monitor, Star, Send, Bot, User as UserIcon, Clock,
  Brain, Trophy, Target, Sparkles, CheckCircle, AlertCircle,
  RefreshCw, ExternalLink, Globe, Download, Save
} from 'lucide-react';

import Button from '../components/ui/Button';
import ProgressBar from '../components/ui/ProgressBar';
import Loading from '../components/common/Loading';
import CameraEngagementMonitor from '../components/CameraEngagementMonitor'; // ✅ NEW IMPORT
import { notesAPI, progressAPI, roadmapAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';


const LessonPage = () => {
  const { moduleId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const roadmapIdFromQuery = searchParams.get('roadmapId');
  const trackedMinutesRef = useRef(0);
  
  // ✅ NEW: Video ref for engagement monitoring
  const videoRef = useRef(null);
  const [isVideoActive, setIsVideoActive] = useState(false);

  const [state, setState] = useState({
    module: null,
    roadmapId: null,
    selectedResource: null,
    selectedResourceIndex: 0,
    lessonProgress: 0,
    currentStep: 1,
    totalSteps: 2,
    resourceProgress: {},
    completedResources: [],
    timeSpent: 0,
    sessionStartTime: Date.now(),
    isLoading: true,
    isUpdating: false,
    error: null,
    chatOpen: false,
    chatMessages: [{
      type: 'bot',
      message: "Hi! I'm your AI mentor. Ask me anything about this lesson! 🎓",
      timestamp: new Date(),
      isTyping: false
    }],
    chatInput: '',
    isChatLoading: false
  });
  const [notes, setNotes] = useState('');
  const [notesMeta, setNotesMeta] = useState({
    isLoading: true,
    isSaving: false,
    isDirty: false,
    updatedAt: null
  });

  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 300], [0, -100]);

  // 🔥 FETCH MODULE DATA
  useEffect(() => {
    const fetchModuleData = async () => {
      try {
        setState(prev => ({ ...prev, isLoading: true, error: null }));
        console.log('🚀 Fetching lesson for module:', moduleId);

        const roadmaps = await roadmapAPI.getRoadmaps();

        if (!roadmaps || roadmaps.length === 0) {
          throw new Error('No roadmaps found. Please complete onboarding first.');
        }

        let targetRoadmap = null;
        let targetModule = null;
        const roadmapCandidates = roadmapIdFromQuery
          ? roadmaps.filter((roadmap) => String(roadmap.id) === String(roadmapIdFromQuery))
          : roadmaps;

        for (const roadmap of roadmapCandidates) {
          const detailedRoadmap = await roadmapAPI.getRoadmapById(roadmap.id);
          const foundModule = detailedRoadmap.modules?.find(m => m.id === parseInt(moduleId, 10));

          if (foundModule) {
            targetRoadmap = detailedRoadmap;
            targetModule = foundModule;
            break;
          }
        }

        if (!targetModule) {
          throw new Error('Module not found.');
        }

        const resources = targetModule.resources || [];
        const dynamicTotalSteps = Math.max(resources.length, 2);
        const progressPerStep = 100 / dynamicTotalSteps;
        const existingProgress = targetModule.progress || 0;
        const calculatedCurrentStep = Math.max(1, Math.ceil(existingProgress / progressPerStep) || 1);

        console.log('✅ Module loaded:', {
          title: targetModule.title,
          resources: resources.length,
          totalSteps: dynamicTotalSteps
        });

        // 🔥 LOG RESOURCES FOR DEBUGGING
        if (resources.length > 0) {
          console.log('📚 Resources available:');
          resources.forEach((r, i) => {
            console.log(`  ${i + 1}. [${r.type}] ${r.title} - ${r.provider}`);
            console.log(`     URL: ${r.url}`);
          });
        } else {
          console.warn('⚠️ No resources found for this module');
        }

        setState(prev => ({
          ...prev,
          module: targetModule,
          roadmapId: targetRoadmap.id,
          selectedResource: resources[0] || null,
          selectedResourceIndex: 0,
          totalSteps: dynamicTotalSteps,
          currentStep: calculatedCurrentStep,
          lessonProgress: existingProgress,
          isLoading: false
        }));

      } catch (error) {
        console.error('❌ Failed to fetch module:', error);
        setState(prev => ({
          ...prev,
          error: error.message,
          isLoading: false
        }));
      }
    };

    if (moduleId) {
      fetchModuleData();
    }
  }, [moduleId, roadmapIdFromQuery]);

  useEffect(() => {
    const loadNotes = async () => {
      if (!state.roadmapId || !state.module?.id) {
        return;
      }

      try {
        setNotesMeta(prev => ({ ...prev, isLoading: true }));
        const note = await notesAPI.getModuleNotes(state.roadmapId, state.module.id);
        setNotes(note.content || '');
        setNotesMeta({
          isLoading: false,
          isSaving: false,
          isDirty: false,
          updatedAt: note.updatedAt || null
        });
      } catch (error) {
        console.error('Failed to load notes:', error);
        setNotesMeta(prev => ({ ...prev, isLoading: false }));
        toast.error('Failed to load notes');
      }
    };

    loadNotes();
  }, [state.roadmapId, state.module?.id]);

  const saveNotes = useCallback(async (content, silent = false) => {
    if (!state.roadmapId || !state.module?.id) {
      return;
    }

    try {
      setNotesMeta(prev => ({ ...prev, isSaving: true }));
      const savedNote = await notesAPI.saveModuleNotes(state.roadmapId, state.module.id, content);
      setNotesMeta(prev => ({
        ...prev,
        isSaving: false,
        isDirty: false,
        updatedAt: savedNote.updatedAt || new Date().toISOString()
      }));
      if (!silent) {
        toast.success('Notes saved');
      }
    } catch (error) {
      console.error('Failed to save notes:', error);
      setNotesMeta(prev => ({ ...prev, isSaving: false }));
      if (!silent) {
        toast.error('Failed to save notes');
      }
    }
  }, [state.roadmapId, state.module?.id]);

  useEffect(() => {
    if (!state.roadmapId || !state.module?.id || !notesMeta.isDirty) {
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      saveNotes(notes, true);
    }, 1200);

    return () => clearTimeout(timeoutId);
  }, [notes, notesMeta.isDirty, saveNotes, state.roadmapId, state.module?.id]);

  // Time tracking
  useEffect(() => {
    const interval = setInterval(() => {
      setState(prev => ({
        ...prev,
        timeSpent: Math.floor((Date.now() - prev.sessionStartTime) / 1000)
      }));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const getTrackedFormat = useCallback((resourceType) => {
    const normalized = String(resourceType || '').toLowerCase();
    if (normalized.includes('video') || normalized.includes('youtube')) return 'video';
    if (normalized.includes('practice')) return 'practice';
    if (normalized.includes('course') || normalized.includes('paid_course')) return 'course';
    return 'reading';
  }, []);

  useEffect(() => {
    if (!state.roadmapId || !state.module?.id) {
      return undefined;
    }

    const syncTimeSpent = async () => {
      const elapsedMinutes = Math.floor(state.timeSpent / 60);
      const unsyncedMinutes = elapsedMinutes - trackedMinutesRef.current;
      if (unsyncedMinutes <= 0) {
        return;
      }

      try {
        await progressAPI.updateTimeSpent(
          state.roadmapId,
          state.module.id,
          getTrackedFormat(state.selectedResource?.type),
          unsyncedMinutes
        );
        trackedMinutesRef.current = elapsedMinutes;
      } catch (error) {
        console.warn('Time tracking sync failed:', error.message);
      }
    };

    const intervalId = setInterval(syncTimeSpent, 60000);

    return () => {
      clearInterval(intervalId);
      syncTimeSpent();
    };
  }, [getTrackedFormat, state.module?.id, state.roadmapId, state.selectedResource?.type, state.timeSpent]);

  // Progress auto-save
  useEffect(() => {
    const moduleIdForSave = state.module?.id;
    if (!state.isLoading && moduleIdForSave && state.roadmapId && state.lessonProgress > 0) {
      const saveProgress = async () => {
        try {
          await roadmapAPI.updateModuleProgress(state.roadmapId, {
            moduleId: moduleIdForSave,
            progress: state.lessonProgress,
            status: state.lessonProgress >= 100 ? 'completed' : 'current',
            completedHours: Math.floor((Date.now() - state.sessionStartTime) / 3600000)
          });
          console.log('✅ Progress auto-saved');
        } catch (error) {
          console.warn('⚠️ Auto-save failed:', error.message);
        }
      };

      const saveInterval = setInterval(saveProgress, 60000);
      return () => clearInterval(saveInterval);
    }
  }, [state.lessonProgress, state.roadmapId, state.module?.id, state.isLoading, state.sessionStartTime]);

  // AI Chat handler
  const handleSendMessage = async () => {
    if (!state.chatInput.trim() || state.isChatLoading) return;

    const userMessage = {
      type: 'user',
      message: state.chatInput,
      timestamp: new Date()
    };

    setState(prev => ({
      ...prev,
      chatMessages: [...prev.chatMessages, userMessage],
      chatInput: '',
      isChatLoading: true
    }));

    setTimeout(() => {
      const responses = [
        `Great question about "${state.module?.title}"! This is fundamental to ${state.module?.skills?.[0] || 'this topic'}.`,
        `In "${state.module?.title}", here's what you need to know...`,
        `Since you're on Step ${state.currentStep}/${state.totalSteps}, let me explain...`,
        `Working with ${state.selectedResource?.title || 'this resource'}, here's the key point...`
      ];

      const botMessage = {
        type: 'bot',
        message: responses[Math.floor(Math.random() * responses.length)],
        timestamp: new Date()
      };

      setState(prev => ({
        ...prev,
        chatMessages: [...prev.chatMessages, botMessage],
        isChatLoading: false
      }));
    }, 1500);
  };

  // Next step handler
  const handleNextStep = async () => {
    const progressPerStep = 100 / state.totalSteps;

    if (state.currentStep < state.totalSteps) {
      const newStep = state.currentStep + 1;
      const newProgress = Math.min(state.lessonProgress + progressPerStep, 100);

      setState(prev => ({
        ...prev,
        currentStep: newStep,
        lessonProgress: newProgress,
        isUpdating: true
      }));

      const resources = state.module.resources || [];
      if (resources.length > 0 && newStep - 1 < resources.length) {
        setState(prev => ({
          ...prev,
          selectedResource: resources[newStep - 1],
          selectedResourceIndex: newStep - 1
        }));
      }

      try {
        await roadmapAPI.updateModuleProgress(state.roadmapId, {
          moduleId: state.module.id,
          progress: newProgress,
          status: newProgress >= 100 ? 'completed' : 'current',
          completedHours: Math.floor(state.timeSpent / 3600)
        });

        setState(prev => ({ ...prev, isUpdating: false }));
        toast.success(`Step ${newStep} completed! 🎉`);

      } catch (error) {
        console.error('Failed to update progress:', error);
        setState(prev => ({ ...prev, isUpdating: false }));
      }

    } else {
      if (state.module.assessment) {
        navigate(`/assessment/${moduleId}?roadmapId=${state.roadmapId}`);
        return;
      }

      try {
        setState(prev => ({ ...prev, isUpdating: true }));
        await roadmapAPI.markModuleComplete(state.roadmapId, state.module.id);
        toast.success('Lesson completed!');
        navigate('/roadmap');
      } catch (error) {
        console.error('Failed to complete:', error);
        setState(prev => ({ ...prev, isUpdating: false }));
        toast.error(error?.message || 'Could not complete module');
      }
    }
  };

  // Resource handlers
  const handleResourceSelect = (resource, index) => {
    console.log('📖 Selected resource:', resource.title);
    setState(prev => ({
      ...prev,
      selectedResource: resource,
      selectedResourceIndex: index,
      resourceProgress: {
        ...prev.resourceProgress,
        [resource.id || index]: Math.min((prev.resourceProgress[resource.id || index] || 0) + 10, 100)
      }
    }));
    
    // ✅ NEW: Activate video monitoring when resource is selected
    if (resource.type?.toLowerCase().includes('video')) {
      setIsVideoActive(true);
      console.log('📹 Video resource selected - Activating engagement monitoring');
    }
  };

  const handleResourceOpen = (resource) => {
    if (resource.url && resource.url !== 'Not Available') {
      console.log('🔗 Opening resource:', resource.url);
      window.open(resource.url, '_blank', 'noopener,noreferrer');
      handleResourceSelect(resource, state.selectedResourceIndex);
      toast.success('Resource opened in new tab! 📖');
      
      // ✅ NEW: Activate monitoring for video resources
      if (resource.type?.toLowerCase().includes('video')) {
        setIsVideoActive(true);
      }
    } else {
      toast.error('Resource URL not available');
    }
  };

  // 🔥 ENHANCED RESOURCE ICON WITH TYPE DETECTION
  const getResourceIcon = (type) => {
    const typeStr = (type || '').toLowerCase();

    if (typeStr.includes('video') || typeStr.includes('youtube')) {
      return <Video className="w-5 h-5 text-red-500" />;
    }
    if (typeStr.includes('doc') || typeStr.includes('article') || typeStr.includes('free_material')) {
      return <FileText className="w-5 h-5 text-blue-500" />;
    }
    if (typeStr.includes('interactive') || typeStr.includes('practice')) {
      return <Monitor className="w-5 h-5 text-green-500" />;
    }
    if (typeStr.includes('course') || typeStr.includes('paid_course')) {
      return <BookOpen className="w-5 h-5 text-purple-500" />;
    }
    return <Globe className="w-5 h-5 text-gray-400" />;
  };

  // 🔥 GET RESOURCE TYPE BADGE COLOR
  const getResourceBadgeColor = (type) => {
    const typeStr = (type || '').toLowerCase();

    if (typeStr.includes('video')) return 'bg-red-500/20 text-red-400 border-red-500/30';
    if (typeStr.includes('doc') || typeStr.includes('free_material')) return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    if (typeStr.includes('practice')) return 'bg-green-500/20 text-green-400 border-green-500/30';
    if (typeStr.includes('course') || typeStr.includes('paid_course')) return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
    return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${secs}s`;
    return `${secs}s`;
  };

  const formatNoteTimestamp = (value) => {
    if (!value) return 'Not saved yet';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Saved';
    return `Saved ${date.toLocaleString()}`;
  };

  const slugify = (value) => (
    (value || 'module-notes')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
  );

  const handleDownloadNotes = () => {
    const content = notes || '';
    const fileName = `aivionlearn-${slugify(state.module?.title || `module-${moduleId}`)}-notes.txt`;
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  // Loading & Error states
  if (state.isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loading />
          <p className="text-white mt-4 text-lg">Loading your lesson...</p>
        </div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center px-4 pt-20">
        <div className="text-center max-w-md">
          <AlertCircle className="w-24 h-24 text-red-400 mx-auto mb-6" />
          <h2 className="text-3xl font-bold text-white mb-4">Error Loading Lesson</h2>
          <p className="text-gray-400 mb-8">{state.error}</p>
          <div className="flex space-x-4 justify-center">
            <Button onClick={() => window.location.reload()} className="bg-purple-600 hover:bg-purple-700">
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </Button>
            <Button onClick={() => navigate('/roadmap')} variant="secondary">
              Back to Roadmap
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!state.module) return <Loading text="Loading content..." />;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden pt-20">

      {/* Header Section */}
      <motion.section className="relative z-10 py-8 px-6" style={{ y: heroY }}>
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between mb-6">
            <Button onClick={() => navigate('/roadmap')} variant="secondary" className="flex items-center">
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>

            <div className="text-center">
              <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent mb-2">
                {state.module.title}
              </h1>
              <p className="text-gray-400 flex items-center justify-center space-x-4">
                <span className="flex items-center">
                  <Clock className="w-4 h-4 mr-1" />
                  Step {state.currentStep}/{state.totalSteps}
                </span>
                <span className="flex items-center">
                  <Target className="w-4 h-4 mr-1" />
                  {formatTime(state.timeSpent)}
                </span>
              </p>
            </div>

            <Button onClick={() => setState(prev => ({ ...prev, chatOpen: !prev.chatOpen }))} className="bg-gradient-to-r from-purple-600 to-pink-600">
              <MessageCircle className="w-5 h-5 mr-2" />
              AI Tutor
            </Button>
          </motion.div>

          {/* Progress Bar */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-slate-800/30 backdrop-blur-xl rounded-2xl p-6 border border-slate-700/50">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-4">
                <Sparkles className="w-5 h-5 text-yellow-400" />
                <span className="text-white font-medium">Lesson Progress</span>
              </div>
              <span className="text-purple-400 font-bold text-xl">{Math.round(state.lessonProgress)}%</span>
            </div>
            <ProgressBar value={state.lessonProgress} className="mb-4" showPercentage={false} />

            <div className="flex justify-between">
              {[...Array(state.totalSteps)].map((_, index) => (
                <div key={index} className={`flex items-center justify-center w-12 h-12 rounded-xl text-sm font-bold transition-all ${
                  index + 1 < state.currentStep ? 'bg-green-500 text-white' :
                  index + 1 === state.currentStep ? 'bg-purple-500 text-white ring-2 ring-purple-400' :
                  'bg-slate-700 text-gray-400'
                }`}>
                  {index + 1 < state.currentStep ? <CheckCircle className="w-6 h-6" /> : index + 1}
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* Main Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

          {/* Main Area */}
          <div className="lg:col-span-2 space-y-8">

            {/* 🔥 ENHANCED RESOURCE DISPLAY WITH VIDEO REF */}
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-slate-800/30 backdrop-blur-xl rounded-2xl border border-slate-700/50 overflow-hidden">
              {state.selectedResource ? (
                <div>
                  {/* ✅ NEW: Video container with ref */}
                  <div ref={videoRef} className="aspect-video bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center relative">
                    <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} 
                      onClick={() => {
                        handleResourceSelect(state.selectedResource, state.selectedResourceIndex);
                        // ✅ Activate monitoring when play is clicked
                        if (state.selectedResource.type?.toLowerCase().includes('video')) {
                          setIsVideoActive(true);
                        }
                      }}
                      className="w-20 h-20 bg-purple-600/80 hover:bg-purple-600 rounded-full flex items-center justify-center cursor-pointer">
                      <Play className="w-8 h-8 text-white ml-1" />
                    </motion.div>
                    
                    {/* ✅ NEW: Video active indicator */}
                    {isVideoActive && state.selectedResource.type?.toLowerCase().includes('video') && (
                      <div className="absolute top-4 right-4 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-2 animate-pulse">
                        <div className="w-2 h-2 bg-white rounded-full animate-ping"></div>
                        AI Monitoring
                      </div>
                    )}
                  </div>

                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-start flex-1">
                        <div className="p-3 bg-purple-500/20 rounded-xl mr-4">
                          {getResourceIcon(state.selectedResource.type)}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="text-xl font-bold text-white">{state.selectedResource.title}</h3>
                            <span className={`px-2 py-1 text-xs rounded-full border ${getResourceBadgeColor(state.selectedResource.type)}`}>
                              {state.selectedResource.type || 'Resource'}
                            </span>
                          </div>
                          <p className="text-gray-400 flex items-center gap-2 flex-wrap">
                            <span>{state.selectedResource.provider}</span>
                            <span>•</span>
                            <span>{state.selectedResource.duration}</span>
                            {state.selectedResource.rating && (
                              <>
                                <Star className="w-4 h-4 text-yellow-400" />
                                <span>{state.selectedResource.rating}</span>
                              </>
                            )}
                            {state.selectedResource.cost === 'free' && (
                              <>
                                <span>•</span>
                                <span className="text-green-400 font-medium">FREE</span>
                              </>
                            )}
                          </p>
                          {state.selectedResource.notes && (
                            <p className="text-gray-500 text-sm mt-2">{state.selectedResource.notes}</p>
                          )}
                        </div>
                      </div>

                      {state.selectedResource.url && state.selectedResource.url !== 'Not Available' && (
                        <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={() => handleResourceOpen(state.selectedResource)}
                          className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm ml-4">
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Open
                        </motion.button>
                      )}
                    </div>

                    <div className="bg-gradient-to-r from-purple-900/20 to-pink-900/20 border border-purple-500/20 rounded-xl p-4">
                      <p className="text-purple-300 text-sm">
                        {state.selectedResource.type === 'video' && '📹 Watch this video to understand core concepts'}
                        {(state.selectedResource.type === 'documentation' || state.selectedResource.type === 'free_material') && '📚 Read this comprehensive documentation'}
                        {state.selectedResource.type === 'practice' && '💻 Practice this module with guided exercises'}
                        {(state.selectedResource.type === 'course' || state.selectedResource.type === 'paid_course') && '🎓 Complete this course for in-depth learning'}
                        {!state.selectedResource.type && '📖 Learn from this educational content'}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-12 text-center">
                  <BookOpen className="w-16 h-16 text-purple-400 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-white mb-2">Resources Loading...</h3>
                  <p className="text-gray-400">Learning materials will appear here</p>
                </div>
              )}
            </motion.div>

            {/* Module Description */}
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-slate-800/30 backdrop-blur-xl rounded-2xl p-8 border border-slate-700/50">
              <h3 className="text-2xl font-bold text-white mb-6 flex items-center">
                <Brain className="w-6 h-6 text-blue-400 mr-3" />
                What You'll Learn
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-slate-700/30 rounded-xl p-6 border border-slate-600/30">
                  <h4 className="text-lg font-semibold text-purple-400 mb-3 flex items-center">
                    <Target className="w-5 h-5 mr-2" />
                    Skills
                  </h4>
                  <ul className="text-gray-300 space-y-2">
                    {state.module.skills?.map((skill, i) => (
                      <li key={i} className="flex items-center">
                        <CheckCircle className="w-4 h-4 text-green-400 mr-2" />
                        {skill}
                      </li>
                    )) || <li>Core concepts</li>}
                  </ul>
                </div>

                <div className="bg-slate-700/30 rounded-xl p-6 border border-slate-600/30">
                  <h4 className="text-lg font-semibold text-blue-400 mb-3 flex items-center">
                    <Trophy className="w-5 h-5 mr-2" />
                    Applications
                  </h4>
                  <p className="text-gray-300 text-sm">
                    Apply {state.module.title.toLowerCase()} in real projects and advance your career.
                  </p>
                </div>
              </div>

              {state.module.description && (
                <div className="bg-gradient-to-r from-purple-900/20 to-pink-900/20 border border-purple-500/20 rounded-xl p-6">
                  <p className="text-gray-300">{state.module.description}</p>
                </div>
              )}
            </motion.div>

            {/* Navigation Buttons */}
            <motion.div className="flex justify-between">
              <Button onClick={() => {
                if (state.currentStep > 1) {
                  setState(prev => ({ 
                    ...prev, 
                    currentStep: prev.currentStep - 1,
                    lessonProgress: Math.max(0, prev.lessonProgress - (100 / state.totalSteps))
                  }));
                }
              }} disabled={state.currentStep <= 1} variant="secondary">
                <ArrowLeft className="w-5 h-5 mr-2" />
                Previous
              </Button>

              <Button onClick={handleNextStep} disabled={state.isUpdating} className="bg-gradient-to-r from-purple-600 to-pink-600">
                {state.isUpdating ? <RefreshCw className="w-5 h-5 mr-2 animate-spin" /> :
                 state.currentStep === state.totalSteps ? <CheckCircle className="w-5 h-5 mr-2" /> :
                 <ChevronRight className="w-5 h-5 mr-2" />}
                {state.currentStep === state.totalSteps ? 'Complete' : 'Next'}
              </Button>
            </motion.div>
          </div>

          {/* 🔥 ENHANCED SIDEBAR WITH BETTER RESOURCE LIST */}
          <div className="space-y-6">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-slate-800/30 backdrop-blur-xl rounded-2xl p-6 border border-slate-700/50">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center">
                <BookOpen className="w-5 h-5 text-green-400 mr-2" />
                Resources ({state.module.resources?.length || 0})
              </h3>

              {state.module.resources && state.module.resources.length > 0 ? (
                <div className="space-y-3">
                  {state.module.resources.map((resource, index) => (
                    <motion.div key={resource.id || index} onClick={() => handleResourceSelect(resource, index)}
                      className={`p-4 rounded-xl cursor-pointer transition-all ${
                        state.selectedResourceIndex === index
                          ? 'bg-purple-600/20 border border-purple-500/30 ring-1 ring-purple-400'
                          : 'bg-slate-700/30 hover:bg-slate-700/50 border border-slate-600/30'
                      }`} whileHover={{ x: 5 }}>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center flex-1 min-w-0">
                          <div className="p-2 bg-purple-500/20 rounded-lg mr-3 flex-shrink-0">
                            {getResourceIcon(resource.type)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-white truncate">{resource.title}</h4>
                            <p className="text-gray-400 text-sm flex items-center gap-2 flex-wrap">
                              <span className="truncate">{resource.provider}</span>
                              <span>•</span>
                              <span>{resource.duration}</span>
                              {resource.cost === 'free' && (
                                <span className="text-green-400 text-xs font-medium">FREE</span>
                              )}
                            </p>
                          </div>
                        </div>

                        {resource.url && resource.url !== 'Not Available' && (
                          <button onClick={(e) => {
                            e.stopPropagation();
                            handleResourceOpen(resource);
                          }} className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors ml-2 flex-shrink-0">
                            <ExternalLink className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-400 text-sm">No resources yet</p>
                </div>
              )}
            </motion.div>

            {/* Session Stats */}
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-slate-800/30 backdrop-blur-xl rounded-2xl p-6 border border-slate-700/50">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center">
                <Trophy className="w-5 h-5 text-yellow-400 mr-2" />
                Stats
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
                  <span className="text-gray-300">Step</span>
                  <span className="text-purple-400 font-bold">{state.currentStep}/{state.totalSteps}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
                  <span className="text-gray-300">Progress</span>
                  <span className="text-green-400 font-bold">{Math.round(state.lessonProgress)}%</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
                  <span className="text-gray-300">Time</span>
                  <span className="text-blue-400 font-bold">{formatTime(state.timeSpent)}</span>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-slate-800/30 backdrop-blur-xl rounded-2xl p-6 border border-slate-700/50">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-white flex items-center">
                  <FileText className="w-5 h-5 text-blue-400 mr-2" />
                  Notes
                </h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => saveNotes(notes)}
                    disabled={notesMeta.isLoading || notesMeta.isSaving}
                    className="p-2 bg-slate-700/60 hover:bg-slate-700 rounded-lg text-gray-200 disabled:opacity-50"
                    title="Save notes"
                  >
                    {notesMeta.isSaving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={handleDownloadNotes}
                    disabled={notesMeta.isLoading}
                    className="p-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white disabled:opacity-50"
                    title="Download notes"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <textarea
                value={notes}
                onChange={(e) => {
                  setNotes(e.target.value);
                  setNotesMeta(prev => ({ ...prev, isDirty: true }));
                }}
                onBlur={() => {
                  if (notesMeta.isDirty) {
                    saveNotes(notes, true);
                  }
                }}
                placeholder={notesMeta.isLoading ? 'Loading notes...' : `Write your ${state.module.title} notes here...`}
                className="w-full min-h-[240px] px-4 py-3 bg-slate-700/40 border border-slate-600/50 rounded-xl text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 resize-none"
              />

              <div className="mt-3 flex items-center justify-between text-xs">
                <span className={`${notesMeta.isDirty ? 'text-yellow-400' : 'text-gray-400'}`}>
                  {notesMeta.isDirty ? 'Unsaved changes' : formatNoteTimestamp(notesMeta.updatedAt)}
                </span>
                <span className="text-gray-500">{notes.length} characters</span>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* AI Chat Modal - KEEPING ORIGINAL */}
      {state.chatOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-6"
          onClick={() => setState(prev => ({ ...prev, chatOpen: false }))}>
          <motion.div initial={{ scale: 0.8 }} animate={{ scale: 1 }} onClick={(e) => e.stopPropagation()}
            className="bg-slate-800/95 backdrop-blur-xl rounded-2xl border border-slate-700/50 w-full max-w-3xl h-[500px] flex flex-col">

            <div className="flex items-center justify-between p-6 border-b border-slate-700">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mr-3">
                  <Bot className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-white">AI Learning Assistant</h3>
                  <p className="text-gray-400 text-sm">Specialized in {state.module.title}</p>
                </div>
              </div>
              <button onClick={() => setState(prev => ({ ...prev, chatOpen: false }))} className="p-2 hover:bg-slate-700/50 rounded-lg text-gray-400 hover:text-white">
                ✕
              </button>
            </div>

            <div className="flex-1 p-6 overflow-y-auto">
              {state.chatMessages.map((msg, i) => (
                <div key={i} className={`flex mb-4 ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`flex items-start max-w-md ${msg.type === 'user' ? 'flex-row-reverse' : ''}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      msg.type === 'user' ? 'bg-purple-600 ml-3' : 'bg-blue-600 mr-3'
                    }`}>
                      {msg.type === 'user' ? <UserIcon className="w-4 h-4 text-white" /> : <Bot className="w-4 h-4 text-white" />}
                    </div>
                    <div className={`p-4 rounded-2xl ${msg.type === 'user' ? 'bg-purple-600 text-white' : 'bg-slate-700 text-gray-100'}`}>
                      <p className="text-sm">{msg.message}</p>
                      <p className="text-xs opacity-70 mt-2">{msg.timestamp.toLocaleTimeString()}</p>
                    </div>
                  </div>
                </div>
              ))}

              {state.isChatLoading && (
                <div className="flex justify-start">
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center mr-3">
                      <Bot className="w-4 h-4 text-white" />
                    </div>
                    <div className="bg-slate-700 p-4 rounded-2xl">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-slate-700">
              <div className="flex space-x-4">
                <input type="text" value={state.chatInput} 
                  onChange={(e) => setState(prev => ({ ...prev, chatInput: e.target.value }))}
                  onKeyPress={(e) => e.key === 'Enter' && !state.isChatLoading && handleSendMessage()}
                  disabled={state.isChatLoading} placeholder={`Ask about ${state.module.title}...`}
                  className="flex-1 px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500" />
                <Button onClick={handleSendMessage} disabled={!state.chatInput.trim() || state.isChatLoading}>
                  {state.isChatLoading ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* ✅ NEW: CAMERA ENGAGEMENT MONITOR */}
      <CameraEngagementMonitor
        videoRef={videoRef}
        userId={user?.id || 1}
        moduleId={parseInt(moduleId) || 1}
        isActive={isVideoActive}
      />
    </div>
  );
};

export default LessonPage;
